import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../layout/dashboardLayout";
import { getSweepsCoinName } from "../../utility/complianceCopy";
import { useComplianceConfig } from "../../utility/useComplianceConfig";
import { formatCoins } from "../../utility/coinFormat";
import { getRedemptionHistory, getRedemptionLimits, submitRedemption } from "../actions/redemption";
import { toastAlert } from "../lib/toastAlert";

const STATUS_LABELS = {
  pending: "Pending review",
  approved: "Approved — transfer pending",
  processing: "Legacy payment processing",
  paid: "Previously paid",
  failed: "Legacy payment needs attention",
  review_required: "Legacy payment needs review",
  rejected: "Rejected",
};

const statusExplanation = (row) => {
  if ((row.status === "review_required" || row.status === "failed") && row.rail === "crypto") {
    return "Our team is checking the payment outcome. No new request is needed.";
  }
  if (row.status === "rejected") {
    return row.statusReason || "The reserved balance has been returned to your wallet.";
  }
  if (row.status === "approved") return "Your request is approved. Payment transfer will be integrated in a future update; no payment has been sent.";
  if (row.status === "paid" && row.rail === "crypto") return "This legacy request was paid before crypto payouts were disabled.";
  return "";
};

function RedemptionHistory({ coinName, history, historyError, historyLoading, onRetry }) {
  return (
    <section className="dts-request-history" aria-labelledby="request-history-heading">
      <div className="dts-history-heading">
        <div>
          <h2 id="request-history-heading">Request history</h2>
        </div>
        {!historyError && !historyLoading && (
          <span>{history.length} {history.length === 1 ? "request" : "requests"}</span>
        )}
      </div>

      {historyLoading ? (
        <p className="dts-history-feedback" role="status">Loading request history…</p>
      ) : historyError ? (
        <div className="dts-history-error" role="alert">
          <p>{historyError}</p>
          <button type="button" className="dts-text-button" onClick={onRetry}>Try again</button>
        </div>
      ) : history.length ? (
        <div className="dts-history-list">
          {history.map((row) => (
            <article className="dts-history-item" key={row.reqId}>
              <div className="dts-history-item__top">
                <div>
                  <span className="dts-history-item__label">Request ID</span>
                  <strong>{row.reqId}</strong>
                </div>
                <span className={`dts-history-status dts-history-status--${String(row.status).toLowerCase()}`}>
                  {STATUS_LABELS[row.status] || "Status unavailable"}
                </span>
              </div>
              <div className="dts-history-item__meta">
                <span><small>Amount</small><strong>{formatCoins(row.amountDc)} {coinName}</strong></span>
                <span><small>Submitted</small><strong>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "Date unavailable"}</strong></span>
                <span><small>Payment transfer</small><strong>{row.rail === "crypto" ? "Legacy request" : "Not available yet"}</strong></span>
              </div>
              {statusExplanation(row) && <p className="dts-history-item__explanation">{statusExplanation(row)}</p>}
              {row.status === "paid" && row.payoutTransactionId && (
                <a href={`https://solscan.io/tx/${encodeURIComponent(row.payoutTransactionId)}`} target="_blank" rel="noopener noreferrer">
                  View payment transaction
                </a>
              )}
            </article>
          ))}
        </div>
      ) : (
        <div className="dts-history-empty">
          <span className="dts-history-empty__mark" aria-hidden="true">—</span>
          <p>No prize requests yet</p>
          <small>Requests you submit will appear here with their latest status.</small>
        </div>
      )}
    </section>
  );
}

export default function Redemption() {
  useComplianceConfig();
  const [amount, setAmount] = useState("");
  const [limits, setLimits] = useState(null);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [availabilityError, setAvailabilityError] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [historyLoading, setHistoryLoading] = useState(true);
  const [loadingLimits, setLoadingLimits] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadingLimits(true);
    setHistoryLoading(true);
    const [limitsResult, historyResult] = await Promise.allSettled([
      getRedemptionLimits(),
      getRedemptionHistory(),
    ]);

    if (limitsResult.status === "fulfilled" && limitsResult.value?.status) {
      setLimits(limitsResult.value.result);
      setAvailabilityError("");
    } else {
      const result = limitsResult.status === "fulfilled" ? limitsResult.value : null;
      setLimits(null);
      setAvailabilityError(
        result?.message || "We could not confirm prize-request availability. Please try again."
      );
    }

    if (historyResult.status === "fulfilled" && historyResult.value?.status) {
      setHistory(historyResult.value.result || []);
      setHistoryError("");
    } else {
      const result = historyResult.status === "fulfilled" ? historyResult.value : null;
      setHistoryError(result?.message || "Request history is unavailable. Please try again.");
    }

    setLoadingLimits(false);
    setHistoryLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const coinName = getSweepsCoinName();
  const redeemableBalance = Number(limits?.redeemableDc || 0);
  const minimumRedemption = Number(limits?.minDc || 0);
  const policyMaximum = Number(limits?.maxDc || 0);
  const maximumRequest = Math.min(policyMaximum, redeemableBalance);
  const canRequest = Boolean(limits) && redeemableBalance >= minimumRedemption;
  const balanceNeeded = Math.max(0, minimumRedemption - redeemableBalance);
  const minimumProgress = minimumRedemption > 0
    ? Math.min(100, (redeemableBalance / minimumRedemption) * 100)
    : 100;

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !limits || !canRequest) return;

    const requestedAmount = Number(amount);
    setMessage("");
    if (!Number.isFinite(requestedAmount) || requestedAmount < minimumRedemption) {
      setMessage(`Enter at least ${formatCoins(minimumRedemption)} ${coinName}.`);
      return;
    }
    if (requestedAmount > maximumRequest) {
      setMessage(`You can request up to ${formatCoins(maximumRequest)} ${coinName} with your current balance.`);
      return;
    }

    setBusy(true);
    try {
      const result = await submitRedemption({ amountDc: requestedAmount });
      setMessage(result.message || (result.status ? "Prize request submitted for review." : "We could not submit your request."));
      if (result.status) {
        const confirmation = result.message || "Prize request submitted for review.";
        const paymentNotice = "Payment transfer will be integrated in a future update; no payment has been sent.";
        toastAlert("success", /payment transfer/i.test(confirmation) ? confirmation : `${confirmation} ${paymentNotice}`);
        setAmount("");
        await load();
      }
    } catch {
      setMessage("We could not submit your request right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <main className="dts-compliance-page dts-redemption">
        <div className="dts-compliance-shell">
          <header className="dts-redemption-header">
            <div>
              <h1>Request a prize</h1>
              <p className="dts-stage9-lead">
                Use cleared {coinName} to submit a prize request. Payment transfer will be integrated in a future update.
              </p>
            </div>
          </header>

          {availabilityError && (
            <div role="alert" className="dts-stage9-alert dts-stage9-alert--warning">
              <span>{availabilityError}</span>
              {/identit|verif|kyc/i.test(availabilityError) && <a className="dts-redemption-recovery" href="/kyc">Verify identity</a>}
              {!limits && !loadingLimits && <button type="button" className="dts-text-button" onClick={load}>Try again</button>}
            </div>
          )}
          {message && <div role="status" className="dts-stage9-alert dts-stage9-alert--info">{message}</div>}
          {loadingLimits && <div className="dts-redemption-loading" role="status">Checking your redeemable balance…</div>}

          {limits && (
            <>
              <section className="dts-redemption-overview" aria-label="Redemption balance and limits">
                <div className="dts-redemption-overview__balance" aria-label="Available to redeem">
                  <span>Available to redeem</span>
                  <strong>{formatCoins(redeemableBalance)} {coinName}</strong>
                  <p>Cleared balance available for a prize request.</p>
                  {!canRequest && (
                    <div className="dts-redemption-progress">
                      <div className="dts-redemption-progress__heading">
                        <span>Progress to minimum</span>
                        <strong>{formatCoins(redeemableBalance)} / {formatCoins(minimumRedemption)}</strong>
                      </div>
                      <div
                        className="dts-redemption-progress__track"
                        role="progressbar"
                        aria-label="Progress toward minimum redemption"
                        aria-valuemin={0}
                        aria-valuemax={minimumRedemption}
                        aria-valuenow={Math.min(redeemableBalance, minimumRedemption)}
                        aria-valuetext={`${formatCoins(redeemableBalance)} of ${formatCoins(minimumRedemption)} ${coinName}`}
                      >
                        <span style={{ width: `${minimumProgress}%` }} />
                      </div>
                      <p>You’re {formatCoins(balanceNeeded)} {coinName} short of the minimum.</p>
                    </div>
                  )}
                </div>
                <div className="dts-redemption-overview__limits" aria-label="Request limits">
                  <div>
                    <span>Minimum request</span>
                    <strong>{formatCoins(minimumRedemption)} {coinName}</strong>
                  </div>
                  <div>
                    <span>Per-request limit</span>
                    <strong>{formatCoins(policyMaximum)} {coinName}</strong>
                    <small>Your request also cannot exceed your available balance.</small>
                  </div>
                </div>
                <a href="/wallet" className="dts-redemption-wallet-link">View wallet breakdown</a>
              </section>

              {canRequest ? (
                <div className="dts-redemption-layout">
                  <form onSubmit={submit} className="dts-redemption-form">
                    <div className="dts-form-heading">
                      <div>
                        <h2>New prize request</h2>
                        <p>Your {coinName} will be reserved while the request is reviewed.</p>
                      </div>
                      <span className="dts-redemption-network">Payment not sent yet</span>
                    </div>

                    <div className="dts-form-section">
                      <div className="dts-section-label">
                        <div><h3>Request amount</h3><p>Choose an amount within your available balance.</p></div>
                      </div>
                      <div className="dts-redemption-amount-row">
                        <label htmlFor="redemption-amount">Amount ({coinName})</label>
                        <button type="button" className="dts-redemption-max" onClick={() => setAmount(String(maximumRequest))}>Use maximum</button>
                      </div>
                      <input
                        id="redemption-amount"
                        className="form-control"
                        required
                        type="number"
                        min={minimumRedemption || 1}
                        max={maximumRequest}
                        step="0.01"
                        value={amount}
                        onChange={(event) => setAmount(event.target.value)}
                        placeholder={`${formatCoins(minimumRedemption)} to ${formatCoins(maximumRequest)}`}
                        aria-describedby="redemption-amount-help"
                      />
                      <small id="redemption-amount-help" className="dts-field-note">
                        Minimum {formatCoins(minimumRedemption)}; your current maximum is {formatCoins(maximumRequest)} {coinName}.
                      </small>
                    </div>

                    <div className="dts-form-actions">
                      <button type="submit" className="dts-btn dts-submit-button" disabled={busy}>
                        {busy ? "Submitting request…" : "Submit prize request"}
                      </button>
                      <span>We’ll update your request history when it’s reviewed.</span>
                    </div>
                  </form>

                  <aside className="dts-redemption-aside" aria-labelledby="payout-summary-heading">
                    <h2 id="payout-summary-heading">Before you submit</h2>
                    <dl className="dts-redemption-summary">
                      <div><dt>Request review</dt><dd>Admin review</dd></div>
                      <div><dt>Payment transfer</dt><dd>Future integration</dd></div>
                    </dl>
                    <p>Approval records the decision only. No payment is sent at this time.</p>
                    <p className="dts-redemption-support">Need help? Contact support before submitting another request.</p>
                  </aside>
                </div>
              ) : null}
            </>
          )}

          <RedemptionHistory
            coinName={coinName}
            history={history}
            historyError={historyError}
            historyLoading={historyLoading}
            onRetry={load}
          />
        </div>
      </main>
    </DashboardLayout>
  );
}
